
import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Form, Spinner, Table } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as XLSX from "xlsx";

import {
  fetchBillsByHouseThunk,
  deleteBillThunk,
  clearWaterBillsError,
} from "../../../../store/slices/waterBillsSlice";

import "../../../../css/master-common.css";

// ============================================================
// DATE FORMATTERS
// ============================================================

const formatIndianDateTime = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "-";

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-");
    return `${d}-${m}-${y}`;
  }

  // MySQL DATETIME without timezone: retain original clock time
  const mysqlDateTime = s.match(
    /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/
  );

  if (mysqlDateTime) {
    const [, y, m, day, hours, minutes] = mysqlDateTime;
    let hh = Number(hours);
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;

    return `${day}-${m}-${y} ${String(hh).padStart(2, "0")}:${minutes} ${ampm}`;
  }

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Asia/Kolkata",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(d);

  const get = (type) =>
    parts.find((part) => part.type === type)?.value || "";

  return `${get("day")}-${get("month")}-${get("year")} ${get("hour")}:${get("minute")} ${get("dayPeriod")}`;
};

const formatIndianDate = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return "-";

  const match = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) return `${match[3]}-${match[2]}-${match[1]}`;

  const mysqlDate = s.match(/^(\d{4})-(\d{2})-(\d{2}) /);
  if (mysqlDate) {
    return `${mysqlDate[3]}-${mysqlDate[2]}-${mysqlDate[1]}`;
  }

  const date = new Date(s);
  if (Number.isNaN(date.getTime())) return "-";

  return new Intl.DateTimeFormat("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  })
    .format(date)
    .replace(/\//g, "-");
};

// ============================================================
// EXCEL HELPERS
// ============================================================

const excelNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : value;
};

const excelDate = (value, withTime = false) => {
  if (value === null || value === undefined || value === "") {
    return "";
  }

  const formatted = withTime
    ? formatIndianDateTime(value)
    : formatIndianDate(value);

  return formatted === "-" ? "" : formatted;
};

// ============================================================
// DELETE CONFIRMATION
// ============================================================

const confirmToast = (
  message,
  { yesText = "Delete", noText = "Cancel", onYes, onNo } = {}
) => {
  return toast(
    ({ closeToast }) => (
      <div>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>
          {message}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <button
            type="button"
            className="btn btn-danger btn-sm"
            onClick={() => {
              closeToast?.();
              onYes?.();
            }}
          >
            {yesText}
          </button>

          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            onClick={() => {
              closeToast?.();
              onNo?.();
            }}
          >
            {noText}
          </button>
        </div>
      </div>
    ),
    {
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
    }
  );
};

// ============================================================
// WATER BILLS TAB
// ============================================================

export default function OldBillsTab({
  home_id,
  activeTab,
  onRefreshProfile,
}) {
  const dispatch = useDispatch();

  const { list = [], loading, error, deletingId } = useSelector(
    (s) => s.waterBills
  );

  const [q, setQ] = useState("");

  const isActive = activeTab === "old_bills";

  // ============================================================
  // LOAD BILLS
  // ============================================================

  useEffect(() => {
    if (!isActive || !home_id) return;

    dispatch(fetchBillsByHouseThunk(home_id));
  }, [isActive, home_id, dispatch]);

  // ============================================================
  // ERRORS
  // ============================================================

  useEffect(() => {
    if (!error) return;

    toast.error(String(error));
    dispatch(clearWaterBillsError());
  }, [error, dispatch]);

  // ============================================================
  // SEARCH
  // ============================================================

  const filtered = useMemo(() => {
    const query = String(q || "").toLowerCase().trim();

    if (!query) return list || [];

    return (list || []).filter((x) => {
      const hay = [
        x.wb_id,
        x.qr_code,
        x.family_head,
        x.door_no,
        x.ward_name,
        x.ag_name,
        x.water_type_name,
        x.mtr_type,
        x.slrm_type_name,
        x.slrm_type,
        x.bill_date,
        x.created_on,
        x.last_bill_date,
      ]
        .map((v) => String(v ?? "").toLowerCase())
        .join(" ");

      return hay.includes(query);
    });
  }, [list, q]);

  // ============================================================
  // EXPORT EXCEL
  // ============================================================

  const exportExcel = () => {
    if (!filtered.length) {
      toast.info("No water bills to export");
      return;
    }

    try {
      const data = filtered.map((x, index) => ({
        "Sl No": index + 1,
        "Bill No": x.wb_id ?? "",
        "House No": x.qr_code || x.door_no || "",
        "Name": x.family_head || "",
        "Bill Date": excelDate(x.bill_date, true),
        "Bill Generated Date": excelDate(x.created_on, true),
        "Net Water Bill": excelNumber(x.net_total),
        "Water Total Amount": excelNumber(x.water_total_amount),
        "Water Due": excelNumber(x.old_due),
        "Net SLRM Bill": excelNumber(x.slrm_total),
        "SLRM Fee": excelNumber(x.slrm_fee),
        "SLRM Due": excelNumber(x.slrm_due),
        "Old Reading": excelNumber(x.old_reading),
        "New Reading": excelNumber(x.new_reading),
        "Total Units": excelNumber(x.total_units),
        "Total Days": excelNumber(x.tot_days),
        "Water Rate": x.water_type_name || x.mtr_type || "",
        "Slrm Rate": x.slrm_type_name || x.slrm_type || "",
        "Ward Name": x.ward_name || "",
        "Agent Name": x.ag_name || "",
        "Last Water Bill Date": excelDate(x.last_bill_date),
        "Minimum Usage": excelNumber(x.min_units),
        "Minimum Amount": excelNumber(x.minimum_amt),
        "Extra Usage": excelNumber(x.extra_usage),
        "Extra Amount": excelNumber(x.extra_amt),
        "Reading Charges": excelNumber(x.reading_fee),
        "Other Charges": excelNumber(x.other_fee),
        "Disconnection Charges": excelNumber(x.disc_charge),
        "Reconnection Charges": excelNumber(x.rec_charge),
        "Disconnection Date": excelDate(x.disconnection_date, true),
      }));

      const worksheet = XLSX.utils.json_to_sheet(data);

      worksheet["!cols"] = Object.keys(data[0]).map((header) => {
        const maxValueLength = data.reduce(
          (max, row) =>
            Math.max(max, String(row[header] ?? "").length),
          header.length
        );

        return {
          wch: Math.min(Math.max(maxValueLength + 3, 14), 38),
        };
      });

      const lastCell = XLSX.utils.encode_cell({
        r: data.length,
        c: Object.keys(data[0]).length - 1,
      });

      worksheet["!autofilter"] = {
        ref: `A1:${lastCell}`,
      };

      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Water Bills"
      );

      XLSX.writeFile(
        workbook,
        `Water_Bills_House_${home_id}.xlsx`
      );

      toast.success("Water bills Excel exported successfully");
    } catch (err) {
      console.error("Water Bills Excel Export Error:", err);
      toast.error("Failed to export water bills Excel");
    }
  };

  // ============================================================
  // REFRESH
  // ============================================================

  const onRefresh = () => {
    if (!home_id) return;

    dispatch(fetchBillsByHouseThunk(home_id));
    toast.info("Refreshing...");
  };

  // ============================================================
  // DELETE
  // ============================================================

  const doDelete = async (row) => {
    const r = await dispatch(
      deleteBillThunk({ wb_id: row.wb_id })
    );

    if (deleteBillThunk.fulfilled.match(r)) {
      toast.success(`Bill #${row.wb_id} deleted`);

      dispatch(fetchBillsByHouseThunk(home_id));

      onRefreshProfile?.();
    } else {
      const msg =
        r?.payload?.message ||
        r?.payload ||
        r?.error?.message ||
        "Delete failed";

      toast.error(String(msg));
    }
  };

  const onDelete = (row) => {
    if (!row?.wb_id) return;

    confirmToast(`Delete Bill #${row.wb_id}?`, {
      onYes: () => doDelete(row),
    });
  };

  if (!isActive) return null;

  // ============================================================
  // UI
  // ============================================================

  return (
    <div className="hm-form-page old-bills-tab">
      <style>
        {`
          /* Water Bills only — compact SLRM-like layout */

          .old-bills-tab,
          .old-bills-tab .hm-form-card,
          .old-bills-tab .hm-form-body {
            min-width: 0;
            max-width: 100%;
          }

          .old-bills-tab .master-table-card {
            width: 100%;
            max-width: 100%;
            min-width: 0;
            height: auto !important;
            min-height: 0 !important;
            overflow: hidden;
          }

          .old-bills-tab .master-table-wrap {
            width: 100%;
            max-width: 100%;
            min-width: 0;

            height: auto !important;
            min-height: 0 !important;
            max-height: 450px !important;

            overflow-x: auto !important;
            overflow-y: auto !important;

            position: relative;
            -webkit-overflow-scrolling: touch;
          }

          .old-bills-tab .old-bills-table {
            width: max-content !important;
            min-width: 100% !important;
            table-layout: auto !important;
            margin-bottom: 0 !important;
          }

          .old-bills-tab .old-bills-table th,
          .old-bills-tab .old-bills-table td {
            width: auto !important;
            min-width: 0 !important;
            max-width: none !important;

            white-space: nowrap !important;
            overflow: visible !important;
            text-overflow: clip !important;

            padding: 10px 12px !important;
            vertical-align: middle !important;
            height: auto !important;
          }

          .old-bills-tab .old-bills-table thead th {
            position: sticky;
            top: 0;
            z-index: 2;
            background: #f9fafb !important;
          }

          .old-bills-tab .hm-form-body {
            padding: 12px !important;
          }

          .old-bills-tab .hm-form-header {
            padding: 12px 16px !important;
          }

          .old-bills-tab .hm-header-right {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
          }

          .old-bills-tab .hm-search-box {
            width: 340px;
            max-width: 100%;
          }

          .old-bills-tab .master-table-wrap::-webkit-scrollbar {
            width: 8px;
            height: 8px;
          }

          .old-bills-tab .master-table-wrap::-webkit-scrollbar-track {
            background: #f1f5f9;
          }

          .old-bills-tab .master-table-wrap::-webkit-scrollbar-thumb {
            background: #94a3b8;
            border-radius: 8px;
          }

          @media (max-width: 768px) {
            .old-bills-tab .hm-header-row {
              flex-direction: column;
              align-items: flex-start;
            }

            .old-bills-tab .hm-header-right {
              width: 100%;
              margin-left: 0;
              flex-wrap: wrap;
              justify-content: flex-start;
            }

            .old-bills-tab .hm-search-box {
              width: 100%;
            }

            .old-bills-tab .old-bills-table th,
            .old-bills-tab .old-bills-table td {
              padding: 9px 10px !important;
            }
          }
        `}
      </style>

      <Card className="hm-form-card">
        <Card.Header className="hm-form-header">
          <div className="hm-header-row">
            <div className="hm-header-left">
              <h5 className="mb-1 hm-form-title">
                All Bills
              </h5>

              <div className="hm-form-subtitle">
                Search & manage all water bills (delete if needed).
              </div>
            </div>

            <div className="hm-header-right">
              <Button
                size="sm"
                variant="outline-success"
                className="master-btn"
                onClick={exportExcel}
                disabled={loading || !filtered.length}
              >
                Export Excel
              </Button>

              <Button
                size="sm"
                variant="outline-secondary"
                className="master-btn"
                onClick={onRefresh}
                disabled={loading}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>

              <div className="hm-search-box">
                <Form.Control
                  className="hm-control old-bills-search"
                  size="sm"
                  placeholder="Search bill no / name / ward / agent / type..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </div>
          </div>
        </Card.Header>

        <Card.Body className="hm-form-body">
          {loading ? (
            <div className="hm-form-loading">
              <Spinner animation="border" />
              <div className="mt-2 text-muted">
                Loading bills...
              </div>
            </div>
          ) : (
            <div className="master-table-card">
              <div className="master-table-wrap">
                <Table
                  bordered
                  hover
                  size="sm"
                  className="mb-0 master-eq-table master-sticky master-row-hover old-bills-table"
                >
                  <thead className="table-light">
                    <tr>
                      <th>Action</th>
                      <th>Bill No</th>
                      <th>House No</th>
                      <th>Name</th>
                      <th>Bill Date</th>
                      <th>Bill Generated Date</th>
                      <th>Net Water Bill</th>
                      <th>Water Total Amount</th>
                      <th>Water Due</th>
                      <th>Net SLRM Bill</th>
                      <th>SLRM Fee</th>
                      <th>SLRM Due</th>
                      <th>Old Reading</th>
                      <th>New Reading</th>
                      <th>Total Units</th>
                      <th>Total Days</th>
                      <th>Water Rate</th>
                      <th>Slrm Rate</th>
                      <th>Ward Name</th>
                      <th>Agent Name</th>
                      <th>Last Water Bill Date</th>
                      <th>Minimum Usage</th>
                      <th>Minimum Amount</th>
                      <th>Extra Usage</th>
                      <th>Extra Amount</th>
                      <th>Reading Charges</th>
                      <th>Other Charges</th>
                      <th>Disconnection Charges</th>
                      <th>Reconnection Charges</th>
                      <th>Disconnection Date</th>
                    </tr>
                  </thead>

                  <tbody>
                    {filtered.length === 0 ? (
                      <tr>
                        <td
                          colSpan={30}
                          className="text-center text-muted py-4"
                        >
                          No bills found
                        </td>
                      </tr>
                    ) : (
                      filtered.map((x) => (
                        <tr key={x.wb_id}>
                          <td>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              className="master-btn"
                              onClick={() => onDelete(x)}
                              disabled={
                                String(deletingId) === String(x.wb_id)
                              }
                            >
                              {String(deletingId) === String(x.wb_id)
                                ? "Deleting..."
                                : "Delete"}
                            </Button>
                          </td>
                          <td>{x.wb_id}</td>
                          <td>{x.qr_code || x.door_no || "-"}</td>
                          <td>{x.family_head || "-"}</td>
                          <td>{formatIndianDateTime(x.bill_date)}</td>
                          <td>{formatIndianDateTime(x.created_on)}</td>
                          <td>{x.net_total ?? "-"}</td>
                          <td>{x.water_total_amount ?? "-"}</td>
                          <td>{x.old_due ?? "-"}</td>
                          <td>{x.slrm_total ?? "-"}</td>
                          <td>{x.slrm_fee ?? "-"}</td>
                          <td>{x.slrm_due ?? "-"}</td>
                          <td>{x.old_reading ?? "-"}</td>
                          <td>{x.new_reading ?? "-"}</td>
                          <td>{x.total_units ?? "-"}</td>
                          <td>{x.tot_days ?? "-"}</td>
                          <td>{x.water_type_name || x.mtr_type || "-"}</td>
                          <td>{x.slrm_type_name || x.slrm_type || "-"}</td>
                          <td>{x.ward_name || "-"}</td>
                          <td>{x.ag_name || "-"}</td>
                          <td>{formatIndianDate(x.last_bill_date)}</td>
                          <td>{x.min_units ?? "-"}</td>
                          <td>{x.minimum_amt ?? "-"}</td>
                          <td>{x.extra_usage ?? "-"}</td>
                          <td>{x.extra_amt ?? "-"}</td>
                          <td>{x.reading_fee ?? "-"}</td>
                          <td>{x.other_fee ?? "-"}</td>
                          <td>{x.disc_charge ?? "-"}</td>
                          <td>{x.rec_charge ?? "-"}</td>
                          <td>{formatIndianDateTime(x.disconnection_date)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
